// Case queries + mutations.

import type { CreateCaseRequest } from '@renovation-claim/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';
import { useMe } from './auth';

export type Case = {
  id: string;
  user_id: string;
  status: 'triage' | 'intake' | 'review' | 'paid' | 'closed';
  title: string | null;
  paid_at: string | null;
  /** Set when the user clicks "I'm done uploading" — gates the chat. */
  intake_done_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

export const casesQueryKey = ['cases'] as const;
export const caseQueryKey = (id: string) => ['cases', id] as const;

export function useCases() {
  const me = useMe();
  return useQuery({
    queryKey: casesQueryKey,
    queryFn: () => apiFetch<{ cases: Case[] }>('/cases'),
    select: (d) => d.cases,
    // Don't fetch when signed out — avoids a 401 between sign-out/refresh and
    // the route's redirect-to-login effect.
    enabled: !!me.data?.user,
  });
}

export function useCase(id: string | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: caseQueryKey(id ?? ''),
    queryFn: () => apiFetch<{ case: Case }>(`/cases/${id}`),
    select: (d) => d.case,
    enabled: !!id && !!me.data?.user,
  });
}

/**
 * Mark the file-first intake stage as done, opening up the chat.
 * Idempotent server-side — safe to call multiple times.
 */
export function useMarkIntakeDone(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: true }>(`/cases/${caseId}/intake/complete`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: caseQueryKey(caseId) }),
  });
}

export type CreateCaseError = { kind: 'other'; message: string };

export function useCreateCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCaseRequest) => {
      try {
        return await apiFetch<{ case: Case }>('/cases', { method: 'POST', body });
      } catch (err) {
        throw {
          kind: 'other',
          message: err instanceof Error ? err.message : 'unknown error',
        } satisfies CreateCaseError;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: casesQueryKey });
    },
  });
}
